import React, { useState, useMemo } from "react"
import { Modal, Input, Radio } from "antd"
import { notify } from "shared/ui/Notifications/notify"
import type { RadioChangeEvent } from "antd"
import { useExportMapStore } from "stores/exportMapStore"
import DataQButton from "shared/ui/Buttons/DataQButton.component"

interface ExportMapBulkPrefixModalProps {
  open: boolean
  onClose: () => void
}

/**
 * Modal for bulk adding prefix/suffix to variable codes.
 * Can apply to all variables or only filtered (searched) variables.
 */
const ExportMapBulkPrefixModal: React.FC<ExportMapBulkPrefixModalProps> = ({
  open,
  onClose,
}) => {
  const {
    allVariables,
    searchTerm,
    getFilteredVariables,
    bulkUpdateVariablePrefix,
  } = useExportMapStore()

  const [prefix, setPrefix] = useState("")
  const [suffix, setSuffix] = useState("")
  const [applyTo, setApplyTo] = useState<"all" | "filtered">("filtered")

  // Get filtered variables when there's a search term
  const filteredVariables = useMemo(() => {
    if (!searchTerm.trim()) return []
    return getFilteredVariables()
  }, [searchTerm, getFilteredVariables])

  const hasFilter = searchTerm.trim().length > 0 && filteredVariables.length > 0
  const targetVariables = applyTo === "filtered" && hasFilter
    ? filteredVariables
    : allVariables

  // Generate preview examples
  const previewExamples = useMemo(() => {
    if (!prefix && !suffix) return []
    return targetVariables.slice(0, 3).map((v) => ({
      original: v.variableCode,
      updated: `${prefix}${v.variableCode}${suffix}`,
    }))
  }, [prefix, suffix, targetVariables])

  const handleApplyToChange = (e: RadioChangeEvent) => {
    setApplyTo(e.target.value)
  }

  const handleApply = () => {
    if (!prefix && !suffix) {
      notify.toast.warning("Please enter a prefix or suffix")
      return
    }

    const variableLabels = targetVariables.map((v) => v.fullLabel)
    const updatedCount = bulkUpdateVariablePrefix(variableLabels, prefix, suffix)

    if (updatedCount > 0) {
      notify.toast.success(`Updated ${updatedCount} variable codes`)
    } else {
      notify.toast.info("No variables were updated")
    }

    // Reset and close
    setPrefix("")
    setSuffix("")
    onClose()
  }

  const handleClose = () => {
    setPrefix("")
    setSuffix("")
    setApplyTo("filtered")
    onClose()
  }

  const isApplyDisabled = !prefix && !suffix

  return (
    <Modal
      title="Bulk Edit Variable Codes"
      open={open}
      onCancel={handleClose}
      footer={null}
      width={500}
    >
      <div className="space-y-4">
        {/* Prefix/Suffix Inputs */}
        <div className="flex gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Prefix
            </label>
            <Input
              placeholder="e.g., FT_"
              value={prefix}
              onChange={(e) => setPrefix(e.target.value)}
              maxLength={20}
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Suffix
            </label>
            <Input
              placeholder="e.g., _v2"
              value={suffix}
              onChange={(e) => setSuffix(e.target.value)}
              maxLength={20}
            />
          </div>
        </div>

        {/* Apply To Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Apply to
          </label>
          <Radio.Group value={applyTo} onChange={handleApplyToChange}>
            <div className="flex flex-col gap-2">
              <Radio value="all">
                All variables ({allVariables.length})
              </Radio>
              <Radio value="filtered" disabled={!hasFilter}>
                Filtered variables ({hasFilter ? filteredVariables.length : 0} shown)
                {!hasFilter && (
                  <span className="text-gray-400 text-xs ml-1">
                    (use search to filter)
                  </span>
                )}
              </Radio>
            </div>
          </Radio.Group>
        </div>

        {/* Preview */}
        {previewExamples.length > 0 && (
          <div className="bg-gray-50 p-3 rounded">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preview
            </label>
            <div className="space-y-1 text-sm">
              {previewExamples.map((ex, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-gray-500">{ex.original}</span>
                  <span className="text-gray-400">→</span>
                  <span className="font-medium text-blue-600">{ex.updated}</span>
                </div>
              ))}
              {targetVariables.length > 3 && (
                <div className="text-gray-400 text-xs">
                  ...and {targetVariables.length - 3} more
                </div>
              )}
            </div>
          </div>
        )}

        {/* Info text */}
        <div className="text-xs text-gray-500">
          This will add the prefix and/or suffix to the variable codes.
          {applyTo === "filtered" && hasFilter && (
            <span className="block mt-1">
              Only variables matching your current search filter will be updated.
            </span>
          )}
        </div>

        {/* Footer buttons */}
        <div className="flex flex-row space-x-2 pt-2">
          <DataQButton className="ml-auto" type="primary" onClick={handleClose}>
            Cancel
          </DataQButton>
          <DataQButton
            type="secondary"
            onClick={handleApply}
            isDisabled={isApplyDisabled}
          >
            Apply
          </DataQButton>
        </div>
      </div>
    </Modal>
  )
}

export default ExportMapBulkPrefixModal
